import {
  Box,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Spinner,
  Stack,
  Text,
  VStack,
} from "@chakra-ui/react";
import React from "react";
import { MdLocationOn, MdMyLocation, MdSearch } from "react-icons/md";

import { Button } from "@components/atoms";

type SamedayPickupLocationInputProps = {
  autocompleteInputRef: React.RefObject<HTMLInputElement>;
  errorMessage: string;
  handleUseCurrentLocation: () => void;
  loading: boolean;
  reset: () => void;
  setIsDrawerOpen: (isDrawerOpen: boolean) => void;
  distance?: string;
  suggestions: google.maps.places.AutocompletePrediction[];
  isLoadingSuggestions: boolean;
  handlePlaceSelection: (
    suggestion: google.maps.places.AutocompletePrediction
  ) => void;
};

export const SamedayPickupLocationInput: React.FC<
  SamedayPickupLocationInputProps
> = ({
  autocompleteInputRef,
  errorMessage,
  handleUseCurrentLocation,
  loading,
  reset,
  setIsDrawerOpen,
  distance,
  suggestions,
  isLoadingSuggestions,
  handlePlaceSelection,
}) => (
  <Stack gap="0.25rem">
    <Box position="relative">
      <InputGroup>
        <Input
          ref={autocompleteInputRef}
          disabled={loading}
          placeholder="Search postcode or suburb"
          onChange={reset}
        />
        <InputLeftElement>
          <MdSearch size={24} />
        </InputLeftElement>
        {(loading || isLoadingSuggestions) && (
          <InputRightElement>
            <Spinner size="sm" />
          </InputRightElement>
        )}
      </InputGroup>

      {suggestions.length > 0 && (
        <Box
          position="absolute"
          top="100%"
          left={0}
          right={0}
          backgroundColor="white"
          border="1px solid"
          borderColor="gray.200"
          boxShadow="lg"
          zIndex={1001}
          maxHeight="200px"
          overflowY="auto"
          marginTop="4px"
        >
          <VStack spacing={0} align="stretch">
            {suggestions.map((suggestion) => (
              <Box
                key={suggestion.place_id}
                padding="0.25rem"
                cursor="pointer"
                _hover={{ backgroundColor: "gray.50" }}
                onClick={() => handlePlaceSelection(suggestion)}
                borderBottom="1px solid"
                borderColor="gray.100"
              >
                <HStack spacing={2}>
                  <MdLocationOn size={16} color="#666" />
                  <VStack spacing={0} align="start" flex={1}>
                    <Text fontSize="sm" fontWeight="medium">
                      {suggestion.structured_formatting?.main_text ||
                        suggestion.description}
                    </Text>
                    {suggestion.structured_formatting?.secondary_text && (
                      <Text fontSize="xs" color="gray.500">
                        {suggestion.structured_formatting.secondary_text}
                      </Text>
                    )}
                  </VStack>
                </HStack>
              </Box>
            ))}
          </VStack>
        </Box>
      )}
    </Box>

    {errorMessage && (
      <Text as="span" color="red" fontSize="small">
        {errorMessage}
      </Text>
    )}

    {!distance ? (
      <Stack>
        <Text as="span" fontSize="small">
          Start typing and choose a postcode or suburb from the list
        </Text>
        <Box
          alignItems="center"
          alignSelf="center"
          cursor={loading ? "not-allowed" : "pointer"}
          display="flex"
          gap="0.5rem"
          maxWidth="fit-content"
          onClick={handleUseCurrentLocation}
        >
          <>
            <MdMyLocation size={20} />
            <Text as="span" borderBottom="2px solid black">
              Use my current location
            </Text>
          </>
        </Box>
      </Stack>
    ) : (
      <Button onClick={() => setIsDrawerOpen(false)}>Continue</Button>
    )}
  </Stack>
);
